import { ExecuteFlowConfig } from '../classes/execute-flow/execute-flow-config';
import { TaskMapping } from '../classes/flow-config/task-mapping';
import { MicrotaskType } from '../enums/microtask-types.enum';

export function GetStartTaskID(taskMappings: TaskMapping[], disabledTasks?: string[]) {
	taskMappings = JSON.parse(JSON.stringify(taskMappings));
	const enabledTaskMappings: TaskMapping[] = [];
	//Make sure we are only working with enabled tasks
	if (disabledTasks && disabledTasks.length > 0) {
		for (let i = 0; i < taskMappings.length; i++) {
			const taskMapping = taskMappings[i];
			// If a task is disabled, we need to connect its previous tasks to its next tasks
			if (disabledTasks.includes(taskMapping.sourceTaskID)) {
				const startConnectedTasks = taskMappings.filter(
					x => x.destinationTaskID === taskMapping.sourceTaskID
				);
				startConnectedTasks.forEach(t => {
					t.destinationTaskID = taskMapping.destinationTaskID;
				});
				continue;
			} else {
				enabledTaskMappings.push(taskMapping);
			}
		}
	} else {
		enabledTaskMappings.push(...taskMappings);
	}

	let potentialStartTasks: string[] = [];
	const destTaskIDs: string[] = [];
	for (const taskMapping of enabledTaskMappings) {
		if (taskMapping.sourceTaskID && !potentialStartTasks.includes(taskMapping.sourceTaskID)) {
			potentialStartTasks.push(taskMapping.sourceTaskID);
		}
		if (taskMapping.destinationTaskID && !destTaskIDs.includes(taskMapping.destinationTaskID)) {
			destTaskIDs.push(taskMapping.destinationTaskID);
		}
	}
	if (potentialStartTasks.length === 1) {
		return potentialStartTasks[0];
	}
	//If the source task ID is not mapped in any destination it must be the start
	potentialStartTasks = potentialStartTasks.filter(
		potentialStartTask => !destTaskIDs.includes(potentialStartTask)
	);
	if (potentialStartTasks.length === 1) {
		return potentialStartTasks[0];
	}
	return undefined;
}

export function GetEndTaskIDs(taskMappings: TaskMapping[]) {
	const sourceTaskIDs: string[] = [];
	for (const taskMapping of taskMappings) {
		if (taskMapping.sourceTaskID && !sourceTaskIDs.includes(taskMapping.sourceTaskID)) {
			sourceTaskIDs.push(taskMapping.sourceTaskID);
		}
	}
	if (sourceTaskIDs.length === 1) {
		return taskMappings.map(x => x.sourceTaskID);
	}

	const destTaskIDs: string[] = [];
	for (const taskMapping of taskMappings) {
		//If the task does not have a destination mapping it must be an end task
		if (
			!taskMapping.destinationTaskID &&
			!destTaskIDs.includes(taskMapping.sourceTaskID) &&
			taskMapping.label !== 'Failure'
		) {
			destTaskIDs.push(taskMapping.sourceTaskID);
		}
	}
	return destTaskIDs;
}

export function SortTaskMappings(
	taskMappings: TaskMapping[],
	disabledTasks?: string[],
	sourceTaskID?: string
) {
	if (taskMappings.length === 0) return [];
	const tempTaskMappings = [...taskMappings];
	sourceTaskID ??= GetStartTaskID(tempTaskMappings, disabledTasks);
	if (!sourceTaskID) {
		throw new Error('Could not find the start task.');
	}
	const resultArray: TaskMapping[] = [];
	sortTaskMappings(resultArray, taskMappings, sourceTaskID);
	return resultArray;
}

function sortTaskMappings(
	resultArray: TaskMapping[],
	tempTaskMappings: TaskMapping[],
	sourceTaskID: string
) {
	const relatedTaskMappings = FindRelatedTaskMappings(tempTaskMappings, sourceTaskID);

	for (const relatedTaskMapping of relatedTaskMappings) {
		resultArray.push(relatedTaskMapping);
		if (relatedTaskMapping.destinationTaskID) {
			sortTaskMappings(resultArray, tempTaskMappings, relatedTaskMapping.destinationTaskID);
		}
	}
}

export function FindRelatedTaskMappings(tempTaskMappings: TaskMapping[], sourceTaskID: string) {
	const sourceTaskMappings: TaskMapping[] = [];
	let index = -1;
	do {
		index = tempTaskMappings.findIndex(x => x.sourceTaskID === sourceTaskID);
		if (index >= 0) {
			sourceTaskMappings.push(...tempTaskMappings.splice(index, 1));
		}
	} while (index >= 0);
	return sourceTaskMappings;
}

export function GetSortedTaskIDs(tempTaskMappings: TaskMapping[], disabledTasks?: string[]) {
	const sortedTaskMappings: TaskMapping[] = SortTaskMappings(tempTaskMappings, disabledTasks);
	const sortedTaskIDs: string[] = [];
	for (const sortedTaskMapping of sortedTaskMappings) {
		if (!sortedTaskIDs.includes(sortedTaskMapping.sourceTaskID)) {
			sortedTaskIDs.push(sortedTaskMapping.sourceTaskID);
		}
	}
	return sortedTaskIDs;
}

export function buildBackendTaskCacheKey(flowID: string, taskID: string) {
	return `BackendTaskConfig:${flowID}:${taskID}`;
}

export function unpackExecuteFlowConfig(executeFlowConfig: ExecuteFlowConfig): ExecuteFlowConfig {
	if (
		executeFlowConfig.subTaskConfigs.length === 1 &&
		executeFlowConfig.subTaskConfigs[0].taskType === MicrotaskType.ExecuteFlow
	) {
		executeFlowConfig = new ExecuteFlowConfig(executeFlowConfig.subTaskConfigs[0].config);
		executeFlowConfig = unpackExecuteFlowConfig(executeFlowConfig);
	}
	return executeFlowConfig;
}
